import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { useAppStore } from '@/stores/useAppStore';
import { RatesQueryResult, RatesQueryResultSchema } from '@/types';
import { fetchSameOrigin, getResponseJSON } from '@/utils/api';

import { queryClient } from '../query-client';

async function getRates(token: string): Promise<RatesQueryResult> {
  const response = await fetchSameOrigin(
    `/rates?token=${encodeURIComponent(token)}`
  );
  const data = await getResponseJSON(response);
  const result = RatesQueryResultSchema.parse(data);
  useAppStore.getState().setProgress(100);
  useAppStore.getState().setSelectedCurrency(result.defaultCurrency);
  useAppStore.getState().setLastRefreshed(new Date().toLocaleString());
  return result;
}

export function useRatesQuery(
  options?: Partial<UseQueryOptions<RatesQueryResult, Error>>
): UseQueryResult<RatesQueryResult, Error> {
  const { token } = useAppStore();
  return useQuery<RatesQueryResult, Error>({
    ...options,
    queryKey: ['rates', token],
    queryFn: () => getRates(token ?? ''),
    enabled: !!token,
    // Don't retry on remount if the query previously failed (e.g. expired token)
    retryOnMount: false,
  });
}

export function invalidateRatesQuery(token: string) {
  queryClient.invalidateQueries({
    queryKey: ['rates', token],
  });
}
